import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  DiscountGroupType,
  DiscountMinRequirementType,
  DiscountProvider,
  DiscountValueType,
  ExpirationType,
} from "../discount";

const expireAfterDaysSchema = z.object({
  expirationType: z.literal(ExpirationType.EXPIRATION_DAYS),
  days: z.number(),
});
const expireAfterDateSchema = z.object({
  expirationType: z.literal(ExpirationType.DATE),
  date: z.date(),
});
const expireNeverSchema = z.object({
  expirationType: z.literal(ExpirationType.NEVER),
});
const expireAfterDeliverySchema = z.object({
  expirationType: z.literal(ExpirationType.DELIVERY),
});

export const expirationSchema = z.discriminatedUnion("expirationType", [
  expireAfterDaysSchema,
  expireAfterDateSchema,
  expireNeverSchema,
  expireAfterDeliverySchema,
]);

// Define schemas for discount settings types
const combinesWithSchema = z.object({
  orderDiscounts: z.boolean().optional(),
  productDiscounts: z.boolean().optional(),
  shippingDiscount: z.boolean().optional(),
});

export enum DiscountSettingsType {
  SHOPIFY_BASIC = "SHOPIFY_BASIC",
  SHOPIFY_FREE_SHIPPING = "SHOPIFY_FREE_SHIPPING",
  SHOPIFY_BXGY = "SHOPIFY_BXGY",
  DYNAMIC_RANGE = "DYNAMIC_RANGE",
}

export enum BxgyTriggerType {
  MINIMUM_QUANTITY = "minimumQuantity",
  MINIMUM_AMOUNT = "minimumAmount",
}

export enum DiscountCategory {
  RECOVER = "RECOVER",
  SHOPPER_AI = "SHOPPER_AI",
  MARKETING = "MARKETING",
}

export enum DiscountPurchaseType {
  ONE_TIME = "ONE_TIME",
  SUBSCRIPTION = "SUBSCRIPTION",
  BOTH = "BOTH",
}

export enum DynamicRangeExpirationUnits {
  WEEKS = "weeks",
  DAYS = "days",
  HOURS = "hours",
  MINUTES = "minutes",
}

export type BasicShopifyDiscountConfiguration = z.infer<
  typeof basicShopifyDiscountSchema
>;
const basicShopifyDiscountSchema = z.object({
  settingsType: z.literal(DiscountSettingsType.SHOPIFY_BASIC),
  discountValueType: createEnum(DiscountValueType),
  discountValueAmount: z.number(),
  combinesWith: combinesWithSchema,
  excludeItemsOnSale: z.boolean().optional(),
  appliesToCollections: z.array(z.string()).optional(),
  appliesToProducts: z.array(z.string()).optional(),
  endsAt: z.date().optional(),
  minimumRequirement: z
    .object({
      type: createEnum(DiscountMinRequirementType),
      minimum: z.number(),
    })
    .optional(),
  customerEligibility: createEnum(["all", "segments", "specific"]).optional(),
  limitToCustomers: z.array(z.string()).optional(),
  limitToSegments: z.array(z.string()).optional(),
  totalUsageLimit: z.number().positive().optional(),
  appliesOncePerCustomer: z.boolean().optional(),
  purchaseType: createEnum(DiscountPurchaseType).optional(),
  recurringCycleLimit: z.number().int().min(0).optional(),
  startsAt: z.date().optional(),
});

// Free Shipping discount schema
export type FreeShippingDiscountConfiguration = z.infer<
  typeof freeShippingDiscountSchema
>;
const freeShippingDiscountSchema = z.object({
  settingsType: z.literal(DiscountSettingsType.SHOPIFY_FREE_SHIPPING),
  combinesWith: combinesWithSchema,
  maximumShippingPrice: z.number().optional(),
  countryCodes: z.array(z.string()).optional(),
  minimumRequirement: z
    .object({
      type: createEnum(DiscountMinRequirementType),
      minimum: z.number(),
    })
    .optional(),
  customerEligibility: createEnum(["all", "segments", "specific"]).optional(),
  limitToCustomers: z.array(z.string()).optional(),
  limitToSegments: z.array(z.string()).optional(),
  totalUsageLimit: z.number().positive().optional(),
  appliesOncePerCustomer: z.boolean().optional(),
  purchaseType: createEnum(DiscountPurchaseType).optional(),
  recurringCycleLimit: z.number().int().min(0).optional(),
  startsAt: z.date().optional(),
  endsAt: z.date().optional(),
});

// Buy X Get Y discount schema
const bxgyProductSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
});

const bxgyCollectionSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
});

export type BxgyDiscountConfiguration = z.infer<typeof bxgyDiscountSchema>;
const bxgyDiscountSchema = z.object({
  settingsType: z.literal(DiscountSettingsType.SHOPIFY_BXGY),
  combinesWith: combinesWithSchema,
  customerBuys: z.object({
    triggerType: createEnum(BxgyTriggerType),
    quantity: z.number().optional(),
    amount: z.number().optional(),
    products: z.array(bxgyProductSchema).optional(),
    collections: z.array(bxgyCollectionSchema).optional(),
  }),
  customerGets: z.object({
    products: z.array(bxgyProductSchema).optional(),
    collections: z.array(bxgyCollectionSchema).optional(),
    quantity: z.number(),
    discountValueType: createEnum(DiscountValueType),
    discountValue: z.number(),
  }),
  usesPerOrderLimit: z.number().positive().optional(),
  customerEligibility: createEnum(["all", "segments", "specific"]).optional(),
  limitToCustomers: z.array(z.string()).optional(),
  limitToSegments: z.array(z.string()).optional(),
  totalUsageLimit: z.number().positive().optional(),
  appliesOncePerCustomer: z.boolean().optional(),
  startsAt: z.date().optional(),
  endsAt: z.date().optional(),
});

// Dynamic Range discount input schema - for RPC inputs
// Includes shared SHOPIFY_BASIC fields so they can be configured via the modal
// and passed through to the discount template
export const dynamicRangeDiscountInputSchema = z.object({
  minPercentage: z.number().min(0).max(100),
  maxPercentage: z.number().min(0).max(100),
  codePrefix: z
    .string()
    .min(3, "Prefix must be at least 3 characters")
    .max(20, "Prefix must be 20 characters or less")
    .regex(
      /^[A-Z0-9-]+$/,
      "Prefix must contain only uppercase letters, numbers, and hyphens",
    )
    .optional(),
  expiration: z.object({
    units: createEnum(DynamicRangeExpirationUnits, {
      enumName: "DynamicRangeExpirationUnits",
    }),
    value: z.number().positive(),
  }),
  // Shared SHOPIFY_BASIC fields
  combinesWith: combinesWithSchema.optional(),
  excludeItemsOnSale: z.boolean().optional(),
  appliesToCollections: z.array(z.string()).optional(),
  appliesToProducts: z.array(z.string()).optional(),
  minimumRequirement: z
    .object({
      type: createEnum(DiscountMinRequirementType),
      minimum: z.number(),
    })
    .optional(),
  customerEligibility: createEnum(["all", "segments", "specific"]).optional(),
  limitToCustomers: z.array(z.string()).optional(),
  limitToSegments: z.array(z.string()).optional(),
  totalUsageLimit: z.number().positive().optional(),
  appliesOncePerCustomer: z.boolean().optional(),
  purchaseType: createEnum(DiscountPurchaseType).optional(),
  recurringCycleLimit: z.number().int().min(0).optional(),
  startsAt: z.date().optional(),
});
export type DynamicRangeDiscountInput = z.infer<
  typeof dynamicRangeDiscountInputSchema
>;

// Dynamic Range discount schema - extends SHOPIFY_BASIC with percentage range and per-code expiration
export type DynamicRangeDiscountConfiguration = z.infer<
  typeof dynamicRangeDiscountSchema
>;
const dynamicRangeDiscountSchema = basicShopifyDiscountSchema
  .omit({
    settingsType: true,
    discountValueType: true,
    discountValueAmount: true,
  })
  .extend(dynamicRangeDiscountInputSchema.shape)
  .extend({ settingsType: z.literal(DiscountSettingsType.DYNAMIC_RANGE) });

// Using discriminatedUnion for extensibility
export const discountSettingsSchema = z.discriminatedUnion("settingsType", [
  basicShopifyDiscountSchema,
  freeShippingDiscountSchema,
  bxgyDiscountSchema,
  dynamicRangeDiscountSchema,
]);
export type DiscountSettings = z.infer<typeof discountSettingsSchema>;

export const databaseDiscountSchema = z.object({
  title: z.string(),
  provider: createEnum(DiscountProvider),
  groupType: createEnum(DiscountGroupType),
  discountId: z.string(),
  code: z.string(),
  expiration: expirationSchema,
  discountSettings: discountSettingsSchema.optional(),
  category: createEnum(DiscountCategory).optional(),
});

export type RedoDatabaseDiscount = z.infer<typeof DiscountDbParser>;
export const DiscountDbParser = z.intersection(
  databaseDiscountSchema,
  z.object({
    _id: zExt.objectId(),
    teamId: zExt.objectId(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
  }),
);

export function isDynamicRangeDiscount(
  settings: DiscountSettings,
): settings is DynamicRangeDiscountConfiguration {
  return settings.settingsType === DiscountSettingsType.DYNAMIC_RANGE;
}

export function formatDiscountValue(config: RedoDatabaseDiscount): string {
  const settings = config.discountSettings;
  if (!settings) {
    return "Unknown";
  }

  switch (settings.settingsType) {
    case DiscountSettingsType.SHOPIFY_BASIC:
      if (settings.discountValueType === DiscountValueType.PERCENTAGE) {
        return `${settings.discountValueAmount}% off`;
      }
      return `$${settings.discountValueAmount} off`;

    case DiscountSettingsType.SHOPIFY_FREE_SHIPPING:
      if (settings.maximumShippingPrice) {
        return `Free shipping (up to $${settings.maximumShippingPrice})`;
      }
      return "Free shipping";

    case DiscountSettingsType.SHOPIFY_BXGY: {
      const gets = settings.customerGets;
      const valueStr =
        gets.discountValueType === DiscountValueType.PERCENTAGE
          ? `${gets.discountValue}% off`
          : `$${gets.discountValue} off`;
      return `Buy X to get ${gets.quantity > 1 ? gets.quantity : "an"} item${gets.quantity > 1 ? "s" : ""} ${valueStr}`;
    }

    default:
      return "Unknown, please notify Slack";
  }
}

export function formatExpiration(config: RedoDatabaseDiscount): string {
  const exp = config.expiration;
  switch (exp.expirationType) {
    case ExpirationType.NEVER:
      return "Never";
    case ExpirationType.EXPIRATION_DAYS:
      return `${exp.days} days after creation`;
    case ExpirationType.DATE:
      return new Date(exp.date).toLocaleDateString();
    case ExpirationType.DELIVERY:
      return "After delivery";
    default:
      return "Unknown, please notify Slack";
  }
}

export function formatStacking(config: RedoDatabaseDiscount): string {
  const settings = config.discountSettings;
  if (!settings) {
    return "Does not stack";
  }

  const combines = settings.combinesWith;
  if (!combines) {
    return "Does not stack";
  }

  const stacksWith: string[] = [];
  if (combines.orderDiscounts) {
    stacksWith.push("order");
  }
  if (combines.productDiscounts) {
    stacksWith.push("product");
  }
  if (combines.shippingDiscount) {
    stacksWith.push("shipping");
  }

  switch (stacksWith.length) {
    case 0: {
      return "Does not stack";
    }
    case 1: {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return stacksWith[0][0].toUpperCase() + stacksWith[0].slice(1);
    }
    case 2: {
      const joined = stacksWith.join(" and ");
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return joined[0].toUpperCase() + joined.slice(1);
    }
    default: {
      const last = stacksWith.pop()!;
      const joined = stacksWith.join(", ") + ", and " + last;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return joined[0].toUpperCase() + joined.slice(1);
    }
  }
}
